import { useMemo } from "react";

import { useLeadSubscriptions } from "./useLeadSubscriptions";

type SavedViewAlert = {
  // The lead subscription content id (used as the alertId when editing).
  id: string;
  frequency: "DAILY" | "WEEKLY";
};

/**
 * Derives a saved view's email alert (content id + frequency) from the user's lead
 * subscriptions, replacing the legacy REST getLeadSubscriptionContents call.
 *
 * A view should have a single email alert; if legacy data has both a daily and a
 * weekly alert for the same view, the daily one wins (until the data is cleaned up).
 */
export function useSavedViewAlert(savedViewId?: string): {
  alert: SavedViewAlert | undefined;
  isLoading: boolean;
} {
  const { data: subscriptions, isLoading } = useLeadSubscriptions();

  const alert = useMemo<SavedViewAlert | undefined>(() => {
    if (!savedViewId || !subscriptions) return undefined;

    const matches = subscriptions.flatMap((subscription) =>
      (subscription.views ?? [])
        .filter((view) => view.id === savedViewId)
        .map((view) => ({ id: view.contentId, frequency: subscription.frequency })),
    );

    if (matches.length > 1 && matches.some((match) => match.frequency === "DAILY")) {
      return matches.find((match) => match.frequency === "DAILY");
    }
    return matches[0];
  }, [subscriptions, savedViewId]);

  return { alert, isLoading };
}
